import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from "_hooks";
import { Button, Icon } from "components/atoms";
import { InputControl } from "components/organism";
import { loginSchema } from "_config/formSchemas";
import { Base } from "components/template";
import { fetchWorkspaces, isLoggedIn } from "_config/store/account/account.thunk";
import { getUser, shouldSelectWorkspace, WEB_APP } from "_config/constants/app.constants";
import { setLogin } from "_config/store/account/slices";
import logo from "_utilities/images/logo.png";

const prefix = "v-login";

const Login = () => {
  const { login, user, userIsLoading } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(loginSchema)
  });

  //const { lookAndFeel } = useOnBoardingGs1();

  const initialized = useRef(null);

  // Marca cuando la verificacion inicial de sesion (isLoggedIn) ya resolvio, para
  // decidir entre mostrar el spinner (verificando/con sesion) o el formulario (sin sesion).
  const [sessionChecked, setSessionChecked] = useState(false);

  const onSubmit = (data) => login(data);

  const handlePostLogin = useCallback(async () => {
    const account = getUser(user);
    if (shouldSelectWorkspace(user)) {
      await dispatch(fetchWorkspaces(account.workspacesId));
      navigate("/account/select");
      return;
    }
    window.location.replace(WEB_APP);
  }, [dispatch, navigate, user]);

  const init = useCallback(() => {
    if (!initialized?.current) {
      initialized.current = true;
      dispatch(isLoggedIn());
    }
  }, [dispatch]);

  useEffect(() => {
    return () => {
      setLogin();
    };
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (user !== null) {
      handlePostLogin();
    }
  }, [user, handlePostLogin]);

  // La verificacion inicial resolvio cuando userIsLoading hizo la transicion
  // true->false. loadingSeen garantiza que primero se observo el estado de carga
  // (startLog) antes de aceptar el false como "resuelto": evita marcar sessionChecked
  // en el render inicial (donde userIsLoading aun es false) y mostrar el form antes
  // de tiempo. A partir de aqui, si no hay usuario se muestra el formulario; si lo
  // hay, handlePostLogin ya esta redirigiendo.
  const loadingSeen = useRef(false);

  useEffect(() => {
    if (userIsLoading) {
      loadingSeen.current = true;
      return;
    }
    if (loadingSeen.current) {
      setSessionChecked(true);
    }
  }, [userIsLoading]);

  // No mostrar el formulario hasta resolver la verificacion de sesion. Si aun se
  // verifica (!sessionChecked) o ya hay sesion (user !== null, redirigiendo via
  // handlePostLogin), se muestra el spinner en lugar del form.
  if (!sessionChecked || user !== null) {
    return (
      <Base>
        <div className={`${prefix}__loading flex items-center justify-center w-full py-10`}>
          <Icon className="animate-spin text-primary text-6xl" icon="spinner" />
        </div>
      </Base>
    );
  }

  return (
    <Base
      footer={<>
        <Link target="_blank" to="/terms">Términos de uso</Link>
        <Link target="_blank" to="/privacy">Aviso de privacidad</Link>
      </>}
    >
      <div className={`${prefix} flex flex-wrap flex-col p-3 mx-auto w-full sm:w-2/3 md:w-3/5 lg:w-5/12 max-w-[550px]`}>
        <div className="flex flex-wrap flex-col gap-3">
          <img src={logo} alt="nbsk-logo" className="w-[55%] mx-auto mb-5"/>
          <p className="text-center text-3xl font-bold mx-auto">Inicia sesión</p>
          <p className="text-center text-xl mx-auto mb-5">Nos da gusto verte de nuevo</p>
        
          <form 
            className="flex flex-wrap flex-col gap-3"
            onSubmit={handleSubmit(onSubmit)}
          >

            <InputControl
              errors={errors?.email}
              label={"Correo electrónico"}
              orientation="vertical"
              inputProps={{
                autoComplete: "off",
                placeholder: "Ej. nombre@tuempresa.com",
                ...register("email")}
              }
            />

            <InputControl
              errors={errors?.password}
              label={"Contraseña"}
              orientation="vertical"
              inputProps={{
                autoComplete: "off",
                placeholder: "****",
                type: "password",
                ...register("password")
              }}
            />

            
            <span className="text-gray-800 w-full text-right">
              <Link to="/account/forgot" className="underline">Olvidé mi contraseña</Link>
            </span>
            
            <Button
              className="bg-primary text-white rounded-lg"
              disabled={userIsLoading}
              fullwidth
              pulse
              type="submit"
            >Continuar</Button>

            <span className="text-gray-800 w-full text-center">
              ¿Aún no tienes cuenta? <Link to="/account/signup" className="underline">Regístrate</Link>
            </span>
            {/* 
            <Hr content={"Ó"} />

            <Button
              className="rounded-lg flex flex-column justify-left align-center border-2"
              onClick={() => navigate(`/partner/gs1/login`)}
              style={{
                color: lookAndFeel.secondary.solid,
                border: `1px solid ${lookAndFeel?.secondary?.solid}`
              }}
            >
              <img src={lookAndFeel?.logo?.gs1} alt="" className="max-h-[25px]" />
              <span>Inicia sesión con GS1 MX</span>
            </Button> */}
          </form>
        </div>
        
      </div>
    </Base>
  )
};

export default Login;
